import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Badge
} from '@mui/material';
import {
  Business as BusinessIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Check as CheckIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { BranchRef } from '../../context/AuthContext';

const BranchSwitcher: React.FC = () => {
  const { user, activeBranch, setActiveBranch } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBranchSelect = (branchId: string) => {
    setActiveBranch(branchId);
    handleClose();
  };

  // If no branches are available, don't render anything
  if (!user?.accessibleBranches || user.accessibleBranches.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
      <Tooltip title="تغيير الفرع" arrow>
        <Box
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            backgroundColor: 'primary.light',
            color: 'white',
            borderRadius: 1.5,
            py: 0.5,
            pl: 1.5,
            pr: 0.5,
            '&:hover': {
              backgroundColor: 'primary.main',
            }
          }}
          onClick={handleClick}
        >
          <BusinessIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2" fontWeight="bold" sx={{ mr: 1, maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {activeBranch?.name || 'اختر الفرع'}
          </Typography>
          <ArrowDropDownIcon />
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 1.5,
            minWidth: 220,
            maxWidth: 300,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 2
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold', color: 'text.secondary' }}>
          اختر الفرع
        </Typography>
        <Divider />
        
        {user.accessibleBranches.map((branch: BranchRef) => {
          const isActive = activeBranch?._id === branch._id;
          const isDefault = user.defaultBranch?._id === branch._id;
          
          return (
            <MenuItem 
              key={branch._id} 
              onClick={() => handleBranchSelect(branch._id)}
              selected={isActive}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" color={isActive ? "primary" : "action"} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {branch.name}
                    {isDefault && (
                      <Tooltip title="الفرع الافتراضي" arrow>
                        <StarIcon 
                          sx={{ 
                            ml: 1, 
                            fontSize: 16, 
                            color: 'primary.main' 
                          }} 
                        />
                      </Tooltip>
                    )}
                  </Box>
                } 
              />
              {isActive && <CheckIcon fontSize="small" color="primary" />}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default BranchSwitcher;